/**
 * CR-6243: the branded backdrop shared by the unauthenticated sign-in pages.
 *
 * Login, Forgot Password and Reset Password are one surface — same layout,
 * same white card, same backdrop. The uploaded background image has to apply
 * to all three: branding that disappeared the moment someone clicked "Forgot
 * password?" would read as a broken page, not a design choice.
 *
 * CR-6245: the colour behind it is system-defined. Tenants can no longer set
 * a background colour, so a stored one is never used.
 *
 * Accept Invitation is deliberately NOT a caller — it uses a different
 * treatment (`bg-gray-50 dark:bg-background` with a themed Card).
 *
 * Pair the returned style with {@link AUTH_BG_CLASSES} on the same element.
 *
 * @returns inline style: the system background colour, plus the image when one is set
 *
 * @example
 * <div className={`min-h-screen flex items-center justify-center ${AUTH_BG_CLASSES}`}
 *      style={useAuthPageBackground()}>
 */
import type { CSSProperties } from 'react';
import { DEFAULT_BRANDING, useBranding } from '../contexts/BrandingContext';

/**
 * Sizing for the background image (harmless when no image is set), and
 * CR-6244's `auth-light`: the sign-in card is designed light, on the tenant's
 * backdrop with the light logo, so dark mode's colour mappings leave it alone.
 */
export const AUTH_BG_CLASSES = 'bg-cover bg-center bg-no-repeat auth-light';

export function useAuthPageBackground(): CSSProperties {
  const { branding } = useBranding();

  return {
    // Always set: it is the fallback while the image loads, the backdrop if
    // the image 404s, and the whole background when none is uploaded.
    backgroundColor: DEFAULT_BRANDING.backgroundColor,
    ...(branding.loginImage
      // JSON.stringify quotes and escapes the URL, so a filename containing
      // quotes or parentheses cannot break out of the url() token.
      ? { backgroundImage: `url(${JSON.stringify(branding.loginImage)})` }
      : {}),
  };
}
